<template>
  <v-timeline align-top dense v-if="auditLog">
    <v-timeline-item v-for="auditLogItem of auditLog" :key="auditLogItem.logId" color="blue" lighten-2 :small="true">
      <v-card class="elevation-2">
        <v-card-title class="title pb-0">Updated</v-card-title>
        <v-card-text class="caption" :data-cy="`caption-${auditLogItem.logId}`">
          by {{ formatDisplayName(auditLogItem) }} on
          {{ formatDate(auditLogItem.created) }}
        </v-card-text>
        <v-card-text v-if="auditLogItem.dataType === 'memberTestStep' && memberTestStepList.length > 0">
          <test-step :logId="auditLogItem.logId" :testStep="memberTestStepMap[auditLogItem.dataTypeId]"></test-step>
        </v-card-text>
        <v-card-text>
          <v-data-table
            :headers="headers"
            :hide-default-footer="true"
            :items="auditLogItem.content"
            class="elevation-0"
            disable-pagination
            v-if="auditLogItem.operation === `update`"
          >
            <template v-slot:item="{ item }">
              <tr>
                <td :data-cy="`itemKey-${item.key}`">{{ formatItemKey(item.key) }}</td>
                <td v-if="valueIsHtml(auditLogItem.dataType, item.key)">
                  <embedded-html
                    :value="item.oldValue"
                    :data-cy="`oldValueHtml-${item.key}-${auditLogItem.logId}`"
                  ></embedded-html>
                </td>
                <td
                  v-else-if="valueIsUsername(auditLogItem.dataType, item.key)"
                  :data-cy="`oldValue-${item.key}-${auditLogItem.logId}`"
                >
                  {{ formatUsername(item.oldValue) }}
                </td>
                <td
                  v-else-if="isIndividualSetupArray(auditLogItem.dataType, item.key, item.oldValue)"
                  :data-cy="`oldValue-${item.key}-${auditLogItem.logId}`"
                  v-html="formatNonHtmlValue(auditLogItem.dataType, item.key, item.oldValue)"
                ></td>
                <td :data-cy="`oldValue-${item.key}-${auditLogItem.logId}`" v-else style="white-space: pre-line">
                  {{ formatNonHtmlValue(auditLogItem.dataType, item.key, item.oldValue) }}
                </td>
                <td v-if="valueIsHtml(auditLogItem.dataType, item.key)">
                  <embedded-html
                    :value="item.newValue"
                    :data-cy="`newValueHtml-${item.key}-${auditLogItem.logId}`"
                  ></embedded-html>
                </td>
                <td
                  v-else-if="valueIsUsername(auditLogItem.dataType, item.key)"
                  :data-cy="`newValue-${item.key}-${auditLogItem.logId}`"
                >
                  {{ formatUsername(item.newValue) }}
                </td>
                <td
                  v-else-if="isIndividualSetupArray(auditLogItem.dataType, item.key, item.newValue)"
                  :data-cy="`newValue-${item.key}-${auditLogItem.logId}`"
                  v-html="formatNonHtmlValue(auditLogItem.dataType, item.key, item.newValue)"
                ></td>
                <td :data-cy="`newValue-${item.key}-${auditLogItem.logId}`" v-else style="white-space: pre-line">
                  {{ formatNonHtmlValue(auditLogItem.dataType, item.key, item.newValue) }}
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-timeline-item>
  </v-timeline>
</template>

<script>
import { formatUsername, formatUTCtoPST, formatDateAsHyphenatedYYYYMMDD } from "@/lib/formatters";
import { isIndividualSetupArray, formatIndividualSetupTable } from "@/lib/auditLogFormatters";
import { mapActions, mapGetters, mapState } from "vuex";
import databaseFieldList from "@/store/databaseFieldList";

import EmbeddedHtml from "@/components/embeddedHtml";
import TestStep from "./TestStep.vue";
import { keyBy } from "lodash";

export default {
  components: {
    EmbeddedHtml,
    TestStep,
  },
  computed: {
    memberTestStepList() {
      return this.$store.getters["testStep/memberTestStepList"](this.releaseNoteId);
    },
    memberTestStepMap() {
      if (!this.memberTestStepList) return;
      return keyBy(this.memberTestStepList, "id");
    },
    ...mapGetters({
      userMap: "user/getUserMap",
    }),
    ...mapState({
      auditLog: (state) => state.auditLog.testPlan,
      serviceAreaId: (state) => state.session.serviceArea,
    }),
  },
  async created() {
    try {
      await Promise.all([
        this.fetchAuditLog({ releaseNoteId: this.releaseNoteId, serviceAreaId: this.serviceAreaId }),
        this.getMemberTestStepList({ releaseNoteId: this.releaseNoteId, serviceAreaId: this.serviceAreaId }),
        this.fetchUserList(),
      ]);
    } catch (error) {
      console.error(error);
      this.$notifyError("Error fetching audit log");
    }
  },
  data() {
    return {
      databaseFieldList,
      headers: [
        { text: "Key", sortable: false, width: "20%" },
        { text: "Old Value", sortable: false, width: "40%" },
        { text: "New Value", sortable: false, width: "40%" },
      ],
    };
  },
  methods: {
    formatDate(date) {
      return formatUTCtoPST(date);
    },
    formatDisplayName(auditLogItem) {
      // Use firstName and lastName from backend JOIN if available
      if (auditLogItem.firstName && auditLogItem.lastName) {
        return `${auditLogItem.firstName} ${auditLogItem.lastName}`;
      }
      // Fallback to username if user not found in database
      return auditLogItem.username || "";
    },
    formatItemKey(itemKey) {
      if (this.databaseFieldList["testPlan"][`${itemKey}`]?.label) {
        return this.databaseFieldList["testPlan"][`${itemKey}`].label;
      }
      if (this.databaseFieldList["memberTestStep"][`${itemKey}`]?.label) {
        return this.databaseFieldList["memberTestStep"][`${itemKey}`].label;
      }
      return itemKey;
    },
    formatNonHtmlValue(auditLogItemDataType, itemKey, value) {
      if (this.databaseFieldList[`${auditLogItemDataType}`][`${itemKey}`]?.dataType === "booleanText") {
        return value === 1 ? true : false;
      } else if (this.databaseFieldList[`${auditLogItemDataType}`][`${itemKey}`]?.dataType === "date") {
        return value === null ? "" : formatDateAsHyphenatedYYYYMMDD(value);
      } else if (this.databaseFieldList[`${auditLogItemDataType}`][`${itemKey}`]?.dataType === "array") {
        // Format arrays nicely (remove brackets and quotes)
        if (value === null || value === undefined) {
          return "";
        }
        if (typeof value === "string") {
          try {
            value = JSON.parse(value);
          } catch {
            return value;
          }
        }
        if (Array.isArray(value)) {
          if (value.length === 0) {
            return "";
          }

          // Special formatting for individualSetup (array of objects) - render as mini-table
          if (itemKey === "individualSetup") {
            return formatIndividualSetupTable(value);
          }

          return value.filter((item) => item !== null && item !== undefined).join(", ");
        }
        return value;
      }
      return value;
    },
    formatUsername(username) {
      return formatUsername(username, this.userMap);
    },
    valueIsHtml(auditLogItemDataType, itemKey) {
      if (this.databaseFieldList[`${auditLogItemDataType}`][`${itemKey}`]?.dataType === "richText") {
        return true;
      }
      return false;
    },
    valueIsUsername(auditLogItemDataType, itemKey) {
      return this.databaseFieldList[`${auditLogItemDataType}`][`${itemKey}`]?.dataType === "user";
    },
    isIndividualSetupArray(auditLogItemDataType, itemKey, value) {
      return isIndividualSetupArray(itemKey, value);
    },
    ...mapActions({
      fetchAuditLog: "auditLog/fetchTestPlan",
      getMemberTestStepList: "testStep/memberList",
      fetchUserList: "user/load",
    }),
  },
  props: ["releaseNoteId"],
};
</script>
